import { FieldLines } from './Field';

/**
 * The band across the top of every page.
 *
 * Each page used to build its own, so the height came out of whatever happened to
 * be inside it. Predict has a long title and three steps, Breakdown has a short
 * one, the schedule has a game card, and the result was a box that jumped size as
 * you moved between tabs. The height is fixed here instead, and the content sits
 * inside it, so moving between pages never shifts the layout underneath.
 *
 * Two fills: `blue` for a page describing itself, and `none` for a banner that
 * paints its own background, which is what the rotating game uses to carry team
 * colours.
 */
// A fixed height, not a minimum. With a minimum, the betting banner grew taller
// than the others because its three steps wrap onto more lines, which is exactly
// the jumping this was meant to stop.
export const BANNER_HEIGHT = 'min-h-[320px] md:h-[340px]';

export function PageBanner({
  fill = 'blue',
  style,
  marks,
  children,
}: {
  fill?: 'blue' | 'none';
  style?: React.CSSProperties;
  marks?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`relative flex items-center overflow-hidden text-paper ${BANNER_HEIGHT} ${fill === 'blue' ? 'bg-blue' : ''}`}
      style={style}
    >
      {fill === 'blue' ? <FieldLines /> : marks}
      <div className="container relative w-full py-6 md:py-8">{children}</div>
    </section>
  );
}

/**
 * The left hand side of a banner: what this page is and what it does.
 *
 * `aside` is the optional right hand column, the numbered steps on a feature page
 * or a set of controls on a schedule.
 */
export function BannerHead({
  eyebrow,
  title,
  sub,
  aside,
  children,
}: {
  eyebrow: string;
  title: string;
  sub?: string;
  aside?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className={`grid grid-cols-1 gap-5 ${aside ? 'md:grid-cols-[1.15fr_1fr] md:items-start' : ''}`}>
      <div className="min-w-0">
        <p className="kicker !text-paper/80">{eyebrow}</p>
        {/* smaller than a page title elsewhere, so "Why the game went the way it
            did." does not break with one orphaned word on its own line */}
        <h1 className="display mt-1.5 text-[34px] leading-[0.95] md:text-[44px]">{title}</h1>
        {sub && <p className="mt-2.5 max-w-[48ch] text-[14px] leading-relaxed text-paper/85 md:text-[15px]">{sub}</p>}
        {children}
      </div>
      {/* pinned to the top of its column so the steps sit at the same height on
          every feature page rather than floating with the title's length */}
      {aside && <div className="md:self-start md:pt-7">{aside}</div>}
    </div>
  );
}

/** The numbered how-it-works list that sits beside a feature's title. */
export function BannerSteps({ steps }: { steps: readonly (readonly [string, string])[] }) {
  return (
    <ol>
      {steps.map(([n, t]) => (
        <li key={n} className="flex items-start gap-3 border-t border-white/30 py-2.5">
          <span className="num pt-[3px] text-[12px] text-paper/70">{n}</span>
          <span className="font-cond text-[16px] font-medium uppercase leading-snug tracking-[0.04em] md:text-[17px]">{t}</span>
        </li>
      ))}
    </ol>
  );
}
